import React, { useRef, useEffect, useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import { FiSend, FiEdit2 } from 'react-icons/fi';
import ChatMessage from './ChatMessage';
import styles from './ChatHistory.module.scss';

const ChatHistory = ({
  messages,
  onSendMessage,
  isGenerating,
  currentActivity,
  initialPrompt,
  onEditPrompt,
  onRegeneratePrompt,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isHoveringPrompt, setIsHoveringPrompt] = useState(false);
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const [editedPrompt, setEditedPrompt] = useState('');
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const promptEditRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [inputValue]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isGenerating && inputValue.trim()) {
      onSendMessage(inputValue);
      setInputValue('');
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleEditClick = () => {
    setIsEditingPrompt(true);
    setEditedPrompt(initialPrompt);
  };

  const handleCancelEdit = () => {
    setIsEditingPrompt(false);
    setEditedPrompt('');
    // Reset the content to original
    if (promptEditRef.current) {
      promptEditRef.current.innerHTML = initialPrompt;
    }
  };

  const handlePromptEdit = (e) => {
    // Just store the current content, don't trigger re-render
    setEditedPrompt(e.currentTarget.innerHTML);
  };

  const handleRegenerateClick = () => {
    // Get the current content from the contentEditable div
    const currentContent = promptEditRef.current?.innerHTML || editedPrompt;

    if (onRegeneratePrompt && currentContent.trim()) {
      // Extract plain text from HTML for regeneration
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = currentContent;
      const plainText = tempDiv.textContent || tempDiv.innerText || '';

      onRegeneratePrompt(plainText);
      setIsEditingPrompt(false);
    }
  };

  // Set the content once when entering edit mode and focus
  useEffect(() => {
    if (isEditingPrompt && promptEditRef.current && editedPrompt) {
      promptEditRef.current.innerHTML = editedPrompt;
      // Focus the editable area
      promptEditRef.current.focus();
      // Move cursor to end
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(promptEditRef.current);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, [isEditingPrompt]);

  return (
    <div className={styles.chat_container}>
      {/* Live Prompt Section - Top */}
      {initialPrompt && (
        <div
          className={styles.live_prompt_section}
          onMouseEnter={() => setIsHoveringPrompt(true)}
          onMouseLeave={() => setIsHoveringPrompt(false)}
        >
          <div className={styles.live_prompt_header}>
            <h4 className={styles.live_prompt_title}>Live Prompt</h4>
            {!isEditingPrompt && isHoveringPrompt && !isGenerating && (
              <button
                className={styles.edit_prompt_button}
                onClick={handleEditClick}
                title="Edit and regenerate"
              >
                <FiEdit2 size={14} />
              </button>
            )}
            {isEditingPrompt && (
              <div className={styles.prompt_edit_actions}>
                <button
                  className={styles.cancel_edit_button}
                  onClick={handleCancelEdit}
                  title="Cancel"
                >
                  Cancel
                </button>
                <button
                  className={styles.regenerate_button}
                  onClick={handleRegenerateClick}
                  title="Regenerate with edited prompt"
                >
                  Regenerate
                </button>
              </div>
            )}
          </div>
          {isEditingPrompt ? (
            <div
              ref={promptEditRef}
              className={`${styles.live_prompt_content} ${styles.editing}`}
              contentEditable={true}
              suppressContentEditableWarning={true}
              onInput={handlePromptEdit}
            />
          ) : (
            <div
              className={styles.live_prompt_content}
              dangerouslySetInnerHTML={{ __html: initialPrompt }}
            />
          )}
        </div>
      )}

      {/* Messages Area */}
      <div className={styles.messages_area}>
        {messages.map((message) => (
          <ChatMessage
            key={message.id}
            message={message}
            type={message.type}
          />
        ))}
        {/* Show activity indicator when generating */}
        {currentActivity && (
          <ChatMessage
            message={currentActivity}
            type="activity"
          />
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area - Fixed at Bottom */}
      <div className={styles.input_area}>
        <Form onSubmit={handleSubmit} className={styles.input_form}>
          <div className={styles.input_wrapper}>
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isGenerating ? 'Generating...' : 'Send a message...'}
              className={styles.chat_input}
              disabled={isGenerating}
              rows={1}
            />
            <Button
              type="submit"
              className={styles.send_button}
              disabled={isGenerating || !inputValue.trim()}
              variant=""
            >
              <FiSend size={18} />
            </Button>
          </div>
          <div className={styles.input_hint}>
            Press Enter to send, Shift + Enter for new line
          </div>
        </Form>
      </div>
    </div>
  );
};

export default ChatHistory;
